import { useEffect, useRef, useCallback, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.js';

export interface VideoPlayerState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
  zoom: number;
  isReady: boolean;
}

export interface UseVideoPlayerOptions {
  videoRef: React.RefObject<HTMLVideoElement>;
  waveformContainerRef: React.RefObject<HTMLDivElement>;
  videoUrl?: string;
  audioUrl?: string;
  peaksUrl?: string;
  onTimeUpdate?: (time: number) => void;
  onRegionCreated?: (start: number, end: number) => void;
  /** When true, render a zoomed-in, touch-scrollable waveform suitable for
   *  phone screens, and disable drag-based gestures (drag-to-seek and
   *  drag-to-create-region) so horizontal swipes scroll the container. */
  isMobile?: boolean;
}

// At ~22 px/s the waveform shows ~16s on a 360px screen and ~20s on a 440px
// screen — the window requested for mobile reviewers on ~10 min recordings.
const MOBILE_PX_PER_SEC = 22;

export function useVideoPlayer({
  videoRef,
  waveformContainerRef,
  videoUrl,
  audioUrl,
  peaksUrl,
  onTimeUpdate,
  onRegionCreated,
  isMobile = false,
}: UseVideoPlayerOptions) {
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const regionsRef = useRef<ReturnType<typeof RegionsPlugin.create> | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onRegionCreatedRef = useRef(onRegionCreated);
  // Flag to suppress region-created callback for programmatic addRegion calls
  const isProgrammaticRef = useRef(false);
  // Reference to the current drag-created region (kept visible until cleared)
  const dragRegionRef = useRef<{ remove: () => void } | null>(null);
  // When set, WaveSurfer auto-pauses at this time (used by playSegment)
  const stopAtRef = useRef<number | null>(null);

  const [state, setState] = useState<VideoPlayerState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    playbackRate: 1,
    zoom: isMobile ? MOBILE_PX_PER_SEC : 0,
    isReady: false,
  });

  // Keep callback refs current without triggering re-init
  useEffect(() => { onTimeUpdateRef.current = onTimeUpdate; });
  useEffect(() => { onRegionCreatedRef.current = onRegionCreated; });

  // Initialize WaveSurfer once the DOM elements are available
  useEffect(() => {
    const container = waveformContainerRef.current;
    if (!container) return;

    const video = videoRef.current;
    const mediaUrl = audioUrl ?? videoUrl ?? '';
    if (!mediaUrl) return;

    let destroyed = false;

    const audioEl = new Audio();
    audioEl.preload = 'auto';
    audioElRef.current = audioEl;

    // Pre-set src BEFORE WaveSurfer.create() so the browser starts buffering
    // immediately. WaveSurfer's setSrc(url, blob) has an early-return guard:
    //   if (url && prevSrc === url) return
    // — so when WaveSurfer later sets the same URL, the guard fires and leaves
    // audioEl.src pointing at the streaming URL. Audio is usable as soon as
    // `canplay` fires (typically a few seconds), regardless of waveform state.
    audioEl.src = mediaUrl;

    const regions = RegionsPlugin.create();
    regionsRef.current = regions;

    async function init() {
      let wsOptions: ConstructorParameters<typeof WaveSurfer>[0] = {
        container: container!,
        waveColor: '#8b5cf6',     // violet-500
        progressColor: '#6d28d9', // violet-700
        height: 64,
        normalize: true,
        interact: true,
        plugins: [regions],
        media: audioEl,
        // On mobile, zoom in so the waveform extends beyond the viewport,
        // auto-follow the playhead, and disable drag-to-seek so the
        // container's overflow-x-auto handles horizontal touch-swipes.
        ...(isMobile
          ? {
              minPxPerSec: MOBILE_PX_PER_SEC,
              autoCenter: true,
              dragToSeek: false,
            }
          : {}),
      };

      if (peaksUrl) {
        try {
          // Fetch the tiny peaks.json (~15 KB) — WaveSurfer fires `ready`
          // immediately once peaks + duration are provided, no file decode needed.
          const data = await fetch(peaksUrl).then((r) => r.json()) as {
            peaks: number[][];
            duration: number;
          };
          if (!destroyed) {
            wsOptions = { ...wsOptions, peaks: data.peaks, duration: data.duration };
          }
        } catch {
          // peaks fetch failed — fall back to url-based decode
        }
      }

      if (destroyed) return;

      // Old sessions (no peaksUrl) or failed fetch: pass url for waveform decode.
      // audioEl.src is already set above so audio plays as soon as canplay fires.
      if (!('peaks' in wsOptions)) {
        wsOptions = { ...wsOptions, url: mediaUrl };
      }

      const ws = WaveSurfer.create(wsOptions);
      if (destroyed) { ws.destroy(); return; }
      wavesurferRef.current = ws;

      ws.on('timeupdate', (t: number) => {
        setState((s) => ({ ...s, currentTime: t }));
        onTimeUpdateRef.current?.(t);
        // Auto-stop at segment end (used by playSegment)
        if (stopAtRef.current !== null && t >= stopAtRef.current) {
          stopAtRef.current = null;
          ws.pause();
        }
        // Keep video in sync — correct drift > 300ms
        if (video && Math.abs(video.currentTime - t) > 0.3) {
          video.currentTime = t;
        }
      });

      ws.on('ready', (dur: number) => {
        setState((s) => ({ ...s, duration: dur, isReady: true }));
      });

      ws.on('play', () => {
        setState((s) => ({ ...s, isPlaying: true }));
        if (video) {
          video.currentTime = audioEl.currentTime;
          video.play().catch(() => {});
        }
      });

      ws.on('pause', () => {
        setState((s) => ({ ...s, isPlaying: false }));
        video?.pause();
      });

      ws.on('seeking', (t: number) => {
        if (video) video.currentTime = t;
      });

      // Capture drag-selected regions and forward the time range to the caller.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      regions.on('region-created', (region: any) => {
        if (isProgrammaticRef.current) return;
        if (dragRegionRef.current) {
          dragRegionRef.current.remove();
        }
        dragRegionRef.current = region;
        onRegionCreatedRef.current?.(region.start, region.end);
      });

      // On mobile, swallow the drag-create gesture so horizontal swipes scroll
      // the container instead. Reviewers can still add segments via the
      // "Add segment" button in TranscriptPanel.
      if (!isMobile) {
        regions.enableDragSelection({ color: 'rgba(139, 92, 246, 0.25)' });
      }
    }

    init();

    return () => {
      destroyed = true;
      wavesurferRef.current?.destroy();
      audioEl.src = '';
      wavesurferRef.current = null;
      regionsRef.current = null;
      audioElRef.current = null;
      dragRegionRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waveformContainerRef.current, videoRef.current, audioUrl, videoUrl, peaksUrl, isMobile]);

  /** Seek to start, play, and auto-pause at end. */
  const playSegment = useCallback((start: number, end: number) => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    const dur = ws.getDuration();
    if (dur <= 0) return;
    stopAtRef.current = end;
    ws.seekTo(start / dur);
    ws.play();
  }, []);

  /**
   * Smart play/pause toggle for a segment button:
   * - Playing within segment → pause (preserving position)
   * - Paused within segment (not at end) → resume from current position
   * - Paused at/past segment end, or outside segment → restart from segment start
   * - Playing a different segment → jump to this segment's start and play
   */
  const toggleSegment = useCallback((start: number, end: number) => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    const dur = ws.getDuration();
    if (dur <= 0) return;
    const t = ws.getCurrentTime();
    const playing = ws.isPlaying();
    // Consider "within segment" if current time is between start and 200ms before end
    // (allows resume; past that window it restarts)
    const isWithinSegment = t >= start - 0.05 && t < end - 0.2;

    if (playing && t >= start - 0.05 && t < end) {
      // Currently playing this segment — pause it
      stopAtRef.current = null;
      ws.pause();
    } else if (!playing && isWithinSegment) {
      // Paused mid-segment — resume without seeking
      stopAtRef.current = end;
      ws.play();
    } else {
      // At end, outside segment, or playing elsewhere — restart from segment start
      stopAtRef.current = end;
      ws.seekTo(start / dur);
      ws.play();
    }
  }, []);

  const play = useCallback(() => {
    wavesurferRef.current?.play();
  }, []);

  const pause = useCallback(() => {
    wavesurferRef.current?.pause();
  }, []);

  const playPause = useCallback(() => {
    wavesurferRef.current?.playPause();
  }, []);

  const seekTo = useCallback((time: number) => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    const dur = ws.getDuration();
    if (dur > 0) ws.seekTo(time / dur);
  }, []);

  const skip = useCallback((seconds: number) => {
    wavesurferRef.current?.skip(seconds);
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    wavesurferRef.current?.setPlaybackRate(rate);
    if (videoRef.current) videoRef.current.playbackRate = rate;
    setState((s) => ({ ...s, playbackRate: rate }));
  }, [videoRef]);

  const zoom = useCallback((minPxPerSec: number) => {
    wavesurferRef.current?.zoom(minPxPerSec);
    setState((s) => ({ ...s, zoom: minPxPerSec }));
  }, []);

  const highlightRegion = useCallback(
    (start: number, end: number, color = 'rgba(139, 92, 246, 0.2)') => {
      const regions = regionsRef.current;
      if (!regions) return;
      isProgrammaticRef.current = true;
      // Remove all existing regions (including previous highlights)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      regions.getRegions().forEach((r: any) => r.remove());
      dragRegionRef.current = null;
      regions.addRegion({ start, end, color, drag: false, resize: false });
      isProgrammaticRef.current = false;
    },
    [],
  );

  /** Remove the drag-selection region (call when modal is dismissed) */
  const clearDragRegion = useCallback(() => {
    if (dragRegionRef.current) {
      dragRegionRef.current.remove();
      dragRegionRef.current = null;
    }
  }, []);

  return {
    state,
    play,
    pause,
    playPause,
    playSegment,
    toggleSegment,
    seekTo,
    skip,
    setPlaybackRate,
    zoom,
    highlightRegion,
    clearDragRegion,
    wavesurfer: wavesurferRef,
  };
}
